import { addCssToElement } from '@mbrtargeting/metatag-utils';
import { MetaTag } from '../../loader/essentials/init-metatag.js';
import { logger } from '../../loader/essentials/logger.js';
import { LifecycleAction } from '../modules/adslot-controller/adslot-lifecycle-state.js';
import { Adslot } from '../modules/adslot-controller/adslot.js';
import { EffectFunction } from './index.js';
import { getIdsForAdSlot } from '../utils/adslot-data-store.js';

const log = logger.logGroup({ prefix: 'PinToBottom Effect' });

declare const window: Window & {
    SDG?: MetaTag;
};

/**
 * Creates close button for stickyFooter
 * @param onclick
 */
const createCloseButton = (onclick: () => void): HTMLButtonElement => {
    const button = document.createElement('button');
    button.classList.add('close-sticky-ad');
    button.onclick = onclick;
    button.onkeydown = onclick; //necessary for screen readers to function properly
    return button;
};

//possible conflict with publisher elements, when other sticky elements are positioned at the bottom of the screen, like social media buttons. No solution for this now, besides overwriting sdgStickyFooter class in websiteConfig with important bottom value
const stickyFooterButtonCss = `
    .close-sticky-ad {
        --sdgCloseCross: clamp(8px, 1.25rem, 16px); 
        --sdgCloseHeight: calc(var(--sdgCloseCross) * 2);
        cursor:pointer;
        position:absolute;
        top: calc( -1 * var(--sdgCloseHeight));
        width: calc(var(--sdgCloseCross) * 1.75);
        height: var(--sdgCloseHeight);
        right: 0;
        background-image: url('data:image/svg+xml;charset=utf-8,%3Csvg viewBox="341 8 13 13" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath fill="%234F4F4F" d="M354 9.31L352.69 8l-5.19 5.19L342.31 8 341 9.31l5.19 5.19-5.19 5.19 1.31 1.31 5.19-5.19 5.19 5.19 1.31-1.31-5.19-5.19z" fill-rule="evenodd"/%3E%3C/svg%3E');
        background-size: var(--sdgCloseCross) var(--sdgCloseCross);
        background-position: 60% 50%;
        background-color: #fff;
        background-repeat: no-repeat;
        box-shadow: 0 -1px 1px 0 rgba(0,0,0,0.2);
        border: none;border-radius: 12px 0 0 0;
    }
    .sdgStickyFooter {
        position:fixed;
        z-index:5000;
        bottom:0px;
        left:0px;
        background-color: white;
        box-shadow: 10px 10px 5px 10px rgba(0,0,0,0.2);
        padding-top:4px;width:100%;
        min-height: 54px;
    }
    @media (prefers-color-scheme: dark) {
        .close-sticky-ad {
            background-color: #111;
            box-shadow: 0 -1px 1px 0 rgba(255,255,255,0.2);
        }
        .sdgStickyFooter {
            background-color: #111;
            box-shadow: 10px 10px 5px 10px rgba(255,255,255,0.2);
        }
    }
`;

/**
 * when slot is filled, start stickyFooter by appending closeButton and applying style class "sdgStickyFooter".
 * Clicking the closeButton will unregister the adslot from the website (cleaning up this effect in same step)
 * @param adslot {Adslot}
 */
export const PIN_TO_BOTTOM: EffectFunction = (adslot: Adslot) => {
    const { containerNode, adserverAnchor } = adslot;
    const closeButton = createCloseButton(() => {
        window.SDG!.Publisher.unregisterSlot(adslot.slotName);
    });
    const containerClassList = containerNode.classList;

    //hide until adslot is filled by an actual ad, otherwise we bother user with an empty container
    closeButton.classList.add('sdgCloaked')
    containerNode.classList.add('sdgCloaked');
    const styleNode = addCssToElement(stickyFooterButtonCss, containerNode);
    containerClassList.add('sdgStickyFooter');

    //close button should remain on adslot, for the whole duration of the effect and just be turned visible/invisible on demand
    adserverAnchor.appendChild(closeButton);

    //when ad is starting to render, show adslot
    adslot.lifecycle.onAction(LifecycleAction.FILL, async () => {
        const containerClassList = containerNode.classList;
        const closeButtonClassList = closeButton.classList;

        //if specific SSPs render their own close button and layer, suppress ours
        const { lastSsp } = getIdsForAdSlot(adslot.slotName); //potentially value is not set yet in current ad impression, and might use value from former imp
        if (lastSsp === 'ogury' || lastSsp === 'gumgum' || lastSsp === 'seedtag') {
            log.debug('%s: hide stickyfooter effect. Provided by 3rd party.', [adslot.slotName.toUpperCase()]);
            containerClassList.add('sdgCloaked');
            closeButtonClassList.add('sdgCloaked');
        } else{
            containerNode.style.cssText = ""; //reverse inline styles set by other SSPs
            log.debug('%s: setup stickyfooter effect.', [adslot.slotName.toUpperCase()]);
            closeButtonClassList.remove('sdgCloaked');
            containerClassList.remove('sdgCloaked');
        }
    });
    // clean up all possible css classes set by script. Possible collision with other effects using sdCloaked.
    return () => {
        closeButton.parentNode?.removeChild(closeButton);
        styleNode.parentNode?.removeChild(styleNode);
        containerClassList.remove('sdgStickyFooter');
        containerClassList.remove('sdgCloaked');
    };
};
